import { useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle, Camera, Loader2, CheckCircle2, X, Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { CITIES, buildWhatsAppLink } from "../../lib/translations";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { useLocalePath } from "./LangRouting";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_DAYS = 8;

const inputCls =
  "w-full bg-black/40 border border-white/15 px-5 py-3.5 text-white placeholder:text-zinc-600 focus:outline-none focus:border-gold transition-colors text-sm";

const SERVICE_TO_CATEGORY = (s) => {
  const t = (s || "").toLowerCase();
  if (/sofa|matr|carpet|teppich|polster|sessel|crouch|кріс|килим|дiван|диван|матр|чистка крісла|чистка матрацу|чистка дивану|sofa cleaning|sofa-reinigung|matratzen|carpet/i.test(t)) {
    return "furniture";
  }
  return "auto";
};

const fmtDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const MiniCalendar = ({ value, onChange, minDate, t, category }) => {
  const [view, setView] = useState(() => new Date(value || minDate));
  const [dayStatus, setDayStatus] = useState({});
  useEffect(() => { if (value) setView(new Date(value)); }, [value]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const minIso = fmtDate(minDate);
  const todayIso = fmtDate(new Date());
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const goto = (delta) => setView(new Date(year, month + delta, 1));

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/booking/month-availability?year=${year}&month=${month + 1}&category=${category}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data.days || []).forEach((it) => { map[it.date] = it.status; });
        setDayStatus(map);
      })
      .catch(() => setDayStatus({}));
    return () => { cancelled = true; };
  }, [year, month, category]);

  const dotColor = (status) => {
    if (status === "AVAILABLE") return "bg-emerald-500";
    if (status === "BOOKED") return "bg-red-500";
    return "bg-zinc-700";
  };

  return (
    <div className="bg-black/40 border border-white/15 p-4" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => goto(-1)} aria-label={t.booking.previousMonth}
                className="p-1 text-zinc-400 hover:text-gold">
          <ChevronLeft size={16} />
        </button>
        <div className="text-white text-sm tracking-wider">
          {view.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </div>
        <button type="button" onClick={() => goto(1)} aria-label={t.booking.nextMonth}
                className="p-1 text-zinc-400 hover:text-gold">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-zinc-500 mb-1 text-center">
        {["Mo","Tu","We","Th","Fr","Sa","Su"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const iso = `${year}-${String(month + 1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
          const dateObj = new Date(year, month, d);
          const isSunday = dateObj.getDay() === 0;
          const tooEarly = iso < minIso;
          const isPast = iso < todayIso;
          // Sundays are always bookable — they bypass the 8-day notice window.
          // Inside the notice window (today..minDate-1), normal days look BOOKED
          // (red, non-selectable). Past dates are UNAVAILABLE (gray).
          let status = dayStatus[iso] || "AVAILABLE";
          if (tooEarly) {
            if (isPast) status = "UNAVAILABLE";
            else if (isSunday) status = dayStatus[iso] || "AVAILABLE";
            else status = "BOOKED";
          }
          const sundayException = tooEarly && !isPast && isSunday && status !== "UNAVAILABLE";
          const disabled = (tooEarly && !sundayException) || status === "UNAVAILABLE";
          const selected = value === iso;
          return (
            <button
              type="button"
              key={i}
              disabled={disabled}
              data-testid={`cal-day-${iso}`}
              data-status={status}
              data-sunday={isSunday ? "true" : "false"}
              onClick={() => onChange(iso)}
              className={`relative aspect-square text-xs flex items-center justify-center border transition-colors ${
                selected ? "border-gold bg-gold/15 text-gold"
                : disabled
                  ? (status === "BOOKED"
                      ? "border-red-500/40 bg-red-500/10 text-red-300/70 cursor-not-allowed"
                      : "border-transparent text-zinc-700 cursor-not-allowed")
                  : "border-white/10 text-white hover:border-gold/60"
              }`}
            >
              {d}
              <span className={`absolute bottom-1 right-1 w-1.5 h-1.5 rounded-full ${dotColor(status)}`} />
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] tracking-[0.18em] uppercase text-zinc-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" />
          {t.booking.legend.available}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
          {t.booking.legend.booked}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-zinc-700 inline-block" />
          {t.booking.legend.unavailable}
        </span>
      </div>
    </div>
  );
};

export const BookingForm = () => {
  const { t, lang } = useLang();
  const lp = useLocalePath();
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", city: "",
    service: "", itemDetails: "", message: "",
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + MIN_DAYS);
    return d;
  }, []);
  const [date, setDate] = useState(fmtDate(minDate));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  const category = SERVICE_TO_CATEGORY(form.service);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    if (!date) return;
    setLoadingSlots(true);
    fetch(`${BACKEND_URL}/api/booking/availability?date=${date}&category=${category}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setSlots(data.slots || []);
        setSlot((cur) => (cur && data.slots?.some((s) => s.time_slot === cur && !s.booked && !s.blocked) ? cur : ""));
      })
      .catch(() => setSlots([]))
      .finally(() => !cancelled && setLoadingSlots(false));
    return () => { cancelled = true; };
  }, [date, category]);

  const resetForm = () => {
    setForm({ name: "", phone: "", email: "", address: "", city: "", service: "", itemDetails: "", message: "" });
    setPhotoFiles([]);
    setConsent(false);
    setSlot("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!consent) { setError(t.quote.gdpr); return; }
    if (!slot) { setError(t.booking.pickTime); return; }
    setError("");
    setSubmitting(true);

    let photoUrls = [];
    try {
      if (photoFiles.length > 0) {
        try {
          const fd = new FormData();
          photoFiles.forEach((f) => fd.append("files", f));
          const r = await fetch(`${BACKEND_URL}/api/upload`, { method: "POST", body: fd });
          if (r.ok) {
            const dj = await r.json();
            photoUrls = (dj.urls || []).map((u) => u.startsWith("http") ? u : `${BACKEND_URL}${u}`);
          }
        } catch { /* best-effort */ }
      }

      const res = await fetch(`${BACKEND_URL}/api/booking/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email || null,
          address: form.address || null,
          city: form.city || null,
          service_type: form.service || null,
          item_details: form.itemDetails || null,
          message: form.message || null,
          language: lang,
          photo_urls: photoUrls,
          preferred_date: date,
          preferred_time_slot: slot,
          consent_gdpr: true,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "submit failed");
      }
      resetForm();
      setShowSuccess(true);
    } catch (err) {
      setError(err.message || t.quote.submitError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0a0a0c] border border-white/10 p-7 md:p-10">
      <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-5">
        <div>
          <label className="label-eyebrow block mb-2">{t.quote.name}</label>
          <input data-testid="booking-input-name" required value={form.name}
                 onChange={(e) => update("name", e.target.value)} className={inputCls} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">{t.quote.phone}</label>
            <input data-testid="booking-input-phone" required type="tel" value={form.phone}
                   onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+49 ..." />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">{t.quote.emailLabel}</label>
            <input data-testid="booking-input-email" type="email" value={form.email}
                   onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="you@email.com" />
          </div>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t.booking.address}</label>
          <input data-testid="booking-input-address" value={form.address}
                 onChange={(e) => update("address", e.target.value)} className={inputCls} />
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t.quote.city}</label>
          <Select value={form.city} onValueChange={(v) => update("city", v)}>
            <SelectTrigger data-testid="booking-input-city"
              className="w-full bg-black/40 border-white/15 px-5 py-6 text-white text-sm rounded-none focus:ring-0 focus:ring-offset-0 focus:border-gold data-[placeholder]:text-zinc-600">
              <SelectValue placeholder={t.quote.cityPlaceholder} />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0c] border-white/10 text-white rounded-none max-h-72">
              {CITIES.map((c) => <SelectItem key={c} value={c} className="focus:bg-gold/10 focus:text-gold rounded-none">{c}</SelectItem>)}
              <SelectItem value="Other" className="focus:bg-gold/10 focus:text-gold rounded-none">{t.quote.cityOther}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t.quote.serviceType}</label>
          <Select value={form.service} onValueChange={(v) => update("service", v)}>
            <SelectTrigger data-testid="booking-input-service"
              className="w-full bg-black/40 border-white/15 px-5 py-6 text-white text-sm rounded-none focus:ring-0 focus:ring-offset-0 focus:border-gold data-[placeholder]:text-zinc-600">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0c] border-white/10 text-white rounded-none">
              {t.quote.serviceOptions.map((opt) => (
                <SelectItem key={opt} value={opt} className="focus:bg-gold/10 focus:text-gold rounded-none">{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t.booking.itemDetails}</label>
          <input data-testid="booking-input-item" value={form.itemDetails}
                 onChange={(e) => update("itemDetails", e.target.value)} className={inputCls}
                 placeholder="z.B. BMW X5 2019 / Ecksofa beige" />
        </div>

        <div>
          <label className="label-eyebrow block mb-2">{t.quote.message}</label>
          <textarea data-testid="booking-input-message" value={form.message}
                    onChange={(e) => update("message", e.target.value)} rows={3} className={`${inputCls} resize-none`} />
        </div>

        {/* Date + slots */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow mb-2 flex items-center gap-2">
              <CalendarIcon size={12} /> {t.booking.preferredDate}
            </label>
            <MiniCalendar value={date} onChange={setDate} minDate={minDate} t={t} category={category} />
          </div>
          <div>
            <label className="label-eyebrow mb-2 flex items-center gap-2">
              <Clock size={12} /> {t.booking.preferredTime}
            </label>
            <div className="bg-black/40 border border-white/15 p-4 space-y-2" data-testid="booking-slots">
              {loadingSlots && (
                <p className="text-zinc-500 text-xs">Loading…</p>
              )}
              {!loadingSlots && slots.length === 0 && (
                <p className="text-zinc-500 text-xs">{t.booking.noSlot}</p>
              )}
              {slots.map((s) => {
                const disabled = s.booked || s.blocked;
                const selected = slot === s.time_slot;
                const label = s.booked ? t.booking.booked : s.blocked ? t.booking.blocked : "";
                return (
                  <button
                    type="button"
                    key={s.time_slot}
                    disabled={disabled}
                    onClick={() => setSlot(s.time_slot)}
                    data-testid={`slot-${s.time_slot}`}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs border tracking-wider transition-colors ${
                      selected ? "border-gold text-gold bg-gold/10"
                      : disabled ? "border-white/5 text-zinc-600 cursor-not-allowed line-through"
                      : "border-white/15 text-white hover:border-gold/60"
                    }`}
                  >
                    <span>{s.time_slot}</span>
                    {label && <span className="text-[10px] tracking-[0.25em] uppercase">{label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Photos */}
        <div>
          <label className="label-eyebrow block mb-2">{t.quote.photos}</label>
          <button type="button" data-testid="booking-input-photos" onClick={() => fileRef.current?.click()}
                  className="w-full flex items-center gap-3 px-5 py-4 border border-dashed border-white/20 hover:border-gold transition-colors text-left">
            <Camera size={18} className="text-gold" />
            <span className="flex-1 text-sm text-zinc-300">
              {photoFiles.length > 0 ? `${photoFiles.length} ✓` : t.quote.photos}
            </span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">+</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                 onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))} />
        </div>

        <label className="flex items-start gap-3 text-zinc-400 text-xs leading-relaxed cursor-pointer select-none">
          <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)}
                 data-testid="booking-gdpr-checkbox"
                 className="mt-0.5 w-4 h-4 accent-[#d4af37] bg-black border border-white/20 cursor-pointer flex-shrink-0" />
          <span>{t.quote.gdpr}{" "}
            <Link to={lp("/datenschutz")} className="text-gold hover:underline">{t.footer.datenschutz}</Link>
          </span>
        </label>

        {error && <p data-testid="booking-error" className="text-red-400 text-xs">{error}</p>}

        <button type="submit" disabled={submitting || !consent || !slot} data-testid="booking-submit"
                className="w-full inline-flex items-center justify-center gap-3 bg-gold text-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold shine-hover hover:bg-[#f3e5ab] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {submitting ? <><Loader2 size={16} className="animate-spin" /> {t.quote.uploading}</>
            : <><CalendarIcon size={16} /> {t.booking.submit}</>}
        </button>
      </form>

      {showSuccess && (
        <div data-testid="booking-success-modal"
             className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
             onClick={() => setShowSuccess(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="relative w-full max-w-md bg-[#0a0a0c] border border-gold/40 p-8 md:p-10 shadow-[0_0_80px_-20px_rgba(212,175,55,0.4)]">
            <button type="button" onClick={() => setShowSuccess(false)}
                    className="absolute top-4 right-4 text-zinc-400 hover:text-gold" aria-label="close">
              <X size={18} />
            </button>
            <div className="flex items-center gap-3">
              <CheckCircle2 size={28} className="text-gold" />
              <h3 className="font-heading text-white text-xl md:text-2xl uppercase tracking-tight">
                {t.booking.successTitle}
              </h3>
            </div>
            <span className="block w-12 h-px bg-gold mt-5" />
            <p className="mt-6 text-zinc-300 text-sm leading-relaxed">{t.booking.successMessage}</p>
            <a href={`tel:${t.quote.successPhone.replace(/\s/g, "")}`}
               className="mt-6 inline-flex items-center gap-2 text-gold text-base tracking-wider hover:underline">
              <MessageCircle size={14} /> {t.quote.successPhone}
            </a>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setShowSuccess(false)}
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-white/20 text-white px-6 py-3 text-xs tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors">
                {t.quote.successClose}
              </button>
              <a href={buildWhatsAppLink("Hi PUREGLOSS! ")} target="_blank" rel="noreferrer"
                 className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-black px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#f3e5ab] transition-colors">
                <MessageCircle size={14} /> {t.quote.successOpenWhatsApp}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
